import React from 'react';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';
import type { StatementReconciliation } from '../../lib/supplierStatementApi';
import { TONE } from '../../components/ui/tokens';
import { formatAmount } from './statementDisplay';

/**
 * Does the statement agree with what the Accounts list stores as outstanding? Said plainly on
 * screen AND on paper, because a statement sent to a supplier that quietly disagrees with our own
 * books is worse than one that says so. A difference is diagnostic: it is listed bill by bill and
 * the statement's balance is never adjusted to hide it.
 */
export interface ReconciliationNoticeProps {
  reconciliation: StatementReconciliation;
  closing: number | null;
  currency: string;
}

const BOX = 'statement-reconciliation rounded-lg border px-3 py-2.5 text-sm';

export const ReconciliationNotice: React.FC<ReconciliationNoticeProps> = ({ reconciliation, closing, currency }) => {
  if (!reconciliation.checked) {
    return (
      <div className={`${BOX} ${TONE.neutral}`} data-testid="reconciliation-not-checked">
        <p className="flex items-start gap-2">
          <Info aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
          <span>
            <strong className="font-semibold">Not checked against the Accounts list.</strong> This statement ends
            before today, and the Accounts list shows what is owed today.
          </span>
        </p>
      </div>
    );
  }

  if (reconciliation.matches) {
    return (
      <div className={`${BOX} ${TONE.positive}`} data-testid="reconciliation-matches">
        <p className="flex items-start gap-2">
          <CheckCircle2 aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
          <span>
            <strong className="font-semibold">Agrees with the Accounts list.</strong> The closing balance of{' '}
            {formatAmount(closing)} {currency} is what the bills currently show as outstanding.
          </span>
        </p>
      </div>
    );
  }

  return (
    <div className={`${BOX} ${TONE.attention}`} role="alert" data-testid="reconciliation-differs">
      <p className="flex items-start gap-2">
        <AlertTriangle aria-hidden="true" size={16} className="mt-0.5 shrink-0" />
        <span>
          <strong className="font-semibold">Does not agree with the Accounts list.</strong>{' '}
          {closing === reconciliation.currentOutstanding ? (
            <>
              The totals happen to match ({formatAmount(closing)} {currency}), but the bills below differ from the
              Accounts list and the differences cancel each other out.
            </>
          ) : (
            <>
              This statement closes at {formatAmount(closing)} {currency}, but the bills currently show{' '}
              {formatAmount(reconciliation.currentOutstanding)} {currency} outstanding.
            </>
          )}{' '}
          The statement is not adjusted; the bills below need looking at before this is sent.
        </span>
      </p>
      {reconciliation.differences.length ? (
        <table className="data-table data-table-dense mt-2 w-full text-left">
          <caption className="sr-only">Bills where the statement and the Accounts list disagree</caption>
          <thead>
            <tr className="text-xs">
              <th scope="col">Invoice</th>
              <th scope="col" className="text-right">
                Statement
              </th>
              <th scope="col" className="text-right">
                Accounts list
              </th>
            </tr>
          </thead>
          <tbody>
            {reconciliation.differences.map((row) => (
              <tr key={row.billId}>
                <th scope="row" className="font-medium">
                  {row.invoiceNumber || 'No invoice number'}
                </th>
                <td className="text-right tabular-nums">{formatAmount(row.statement)}</td>
                <td className="text-right tabular-nums">{formatAmount(row.stored)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
};
